import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BucketInfo, ValidateStorageRequest } from 'mbs-ui-shared@models/StorageAccount';
import { BehaviorSubject, Observable } from 'rxjs';
import { OnboardingMode } from 'wizards/services/onboarding/onboarding.constants';
import { AppsSummaryType, CompleteResult, CreateStorageRequest } from './onboarding.types';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private http = inject(HttpClient);

  readonly controllerPath = '/api/gettingstarted';

  public stable$ = new BehaviorSubject(false);
  public nextStepAvailable$ = new BehaviorSubject(true);
  public hasError$ = new BehaviorSubject(false);
  public agentsInProgress$ = new BehaviorSubject([]);

  private agentsInProgress: { [key: string]: boolean } = {};

  addAgentInProgress(type: string): void {
    this.agentsInProgress[type] = true;

    this.agentsInProgress$.next(Object.keys(this.agentsInProgress));
  }

  initialize(): Observable<{ accountId: string; companyId: string }> {
    return this.http.post<{ accountId: string; companyId: string }>(`${this.controllerPath}/initialize`, null);
  }

  complete(result: CompleteResult): Observable<string> {
    return this.http.post<string>(`${this.controllerPath}/complete`, { result });
  }

  configureComputerByHid(hid: string): Observable<string> {
    return this.http.post<string>(`${this.controllerPath}/configcomputer`, { hid });
  }

  mode(): Observable<{ mode: OnboardingMode }> {
    return this.http.get<{ mode: OnboardingMode }>(`${this.controllerPath}/mode`);
  }

  setTimezone(timeZone: string): Observable<null> {
    return this.http.put<null>(`${this.controllerPath}/set-timezone`, { timeZoneIona: timeZone });
  }

  sendAppsSummary(summary: AppsSummaryType): Observable<null> {
    return this.http.post<null>(`${this.controllerPath}/send-apps-summary`, summary);
  }

  createDomain(domain: { domainName: string; sharePointTeamDrives: 0 | 1 }): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.controllerPath}/apps/create-domain`, domain);
  }

  assignSharePoint({ domainName, ownerId }: { domainName: string; ownerId: string }): Observable<null> {
    return this.http.post<null>(`${this.controllerPath}/apps/assign-sharepoint`, {
      domain: domainName,
      ownerId
    });
  }

  createStorage(data: CreateStorageRequest): Observable<null> {
    return this.http.post<null>(`${this.controllerPath}/create-storage`, data);
  }

  validateStorage(data: ValidateStorageRequest): Observable<BucketInfo[]> {
    return this.http.post<BucketInfo[]>(`${this.controllerPath}/validate-storage`, data);
  }

  setRegion(systemName): Observable<null> {
    return this.http.post<null>(`${this.controllerPath}/destination?regionSystemName=${systemName}`, {});
  }
}
